import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import Select from "react-select";
import {
  ReactFlow,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  Handle,
  Position,
  MiniMap,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import {
  Trash2,
  Edit,
  Hash,
  Phone,
  ChevronDown,
  CornerUpLeft,
  SlidersHorizontal,
  Mail,
  Lock,
  CalendarClock,
  Upload,
  Palette,
  QrCode,
  Key,
  SquareArrowOutUpRight,
  Image,
  Text,
  ClipboardType,
  Receipt,
  Save,
  Undo,
  Redo,
  ChevronUp,
  ChevronDown as ChevronDownMove,
  Navigation,
  SquareDashedKanban,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import { handleApiResponse, handleCustomResponse } from "../utils/response";

// Utility Functions
const generateUniqueCode = (label, existingCodes) => {
  let baseCode =
    label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .trim() || "step";
  let uniqueCode = baseCode;
  let counter = 1;
  while (existingCodes.includes(uniqueCode)) {
    uniqueCode = `${baseCode}_${counter}`;
    counter++;
  }
  return uniqueCode;
};

// Pre-defined list of available field components
const htmlComponents = [
  {
    name: "",
    label: "",
    displayName: "Text",
    icon: ClipboardType,
    type: "text",
    validation: "min:0|max:255",
  },
  {
    name: "",
    label: "",
    displayName: "Email",
    icon: Mail,
    type: "email",
    validation: "email",
  },
  {
    name: "",
    label: "",
    displayName: "Number",
    icon: Hash,
    type: "number",
    validation: "min:0|max:1000000",
  },
  {
    name: "",
    label: "",
    displayName: "Phone Number",
    icon: Phone,
    type: "tel",
    validation: "regex:^\\+?[0-9]{7,15}$",
  },
  {
    name: "",
    label: "",
    displayName: "Password",
    icon: Lock,
    type: "password",
    validation: "min:8|max:255",
  },
  {
    name: "",
    label: "",
    displayName: "Textbox",
    icon: Text,
    type: "textarea",
    validation: "min:2|max:1000",
  },
  {
    name: "",
    label: "",
    displayName: "Dropdown",
    icon: ChevronDown,
    type: "select",
    validation: "required",
  },
  {
    name: "",
    label: "",
    displayName: "Plate Number",
    icon: SquareDashedKanban,
    type: "plate_number",
    validation: "",
  },
  {
    name: "",
    label: "",
    displayName: "Date Time",
    icon: CalendarClock,
    type: "datetime",
    validation: "date_format:YYYY-MM-DD HH:mm:ss",
  },
  {
    name: "",
    label: "",
    displayName: "File Upload",
    icon: Upload,
    type: "file",
    validation: "mimes:pdf,doc,docx,jpg,png|max:2048",
  },
  {
    name: "",
    label: "",
    displayName: "Image Upload",
    icon: Image,
    type: "image",
    validation: "image|mimes:jpg,jpeg,png|max:2048",
  },
  {
    name: "",
    label: "",
    displayName: "Color Picker",
    icon: Palette,
    type: "color",
    validation: "regex:^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$",
  },
  {
    name: "",
    label: "",
    displayName: "Range",
    icon: SlidersHorizontal,
    type: "range",
    validation: "min:0|max:100",
  },
  {
    name: "",
    label: "",
    displayName: "QR Scan",
    icon: QrCode,
    type: "scan_qr",
    validation: "nullable",
  },
  {
    name: "",
    label: "",
    displayName: "QR Code",
    icon: QrCode,
    type: "qr_code",
    validation: "nullable",
  },
  {
    name: "",
    label: "",
    displayName: "OTP",
    icon: Key,
    type: "otp",
    validation: "digits:6",
  },
  {
    name: "",
    label: "",
    displayName: "Popup",
    icon: SquareArrowOutUpRight,
    type: "popup",
    validation: "nullable",
  },
  {
    name: "",
    label: "",
    displayName: "Receipt",
    icon: Receipt,
    type: "receipt",
    validation: "nullable",
  },
].map((c, i) => ({
  ...c,
  step_field_id: i + 1,
  required: false,
  options: null,
  order: i + 1,
}));

// Field Configuration Modal
const FieldConfigModal = ({
  isOpen,
  onClose,
  field,
  onSave,
  isEditMode,
  fieldNames,
  optionSources,
}) => {
  const [config, setConfig] = useState({
    name: "",
    label: "",
    required: false,
    validation: "",
    type: "",
    options: "",
    options_source: null,
    options_label_field: null,
    options_value_field: null,
    order: 1,
  });
  const [optionSourceType, setOptionSourceType] = useState("");
  const [selectedMasterTable, setSelectedMasterTable] = useState("");
  const [selectedMasterLabelField, setSelectedMasterLabelField] = useState("");
  const [selectedMasterValueField, setSelectedMasterValueField] = useState("");
  const [selectedApiSource, setSelectedApiSource] = useState("");
  const [selectedApiLabelField, setSelectedApiLabelField] = useState("");
  const [selectedApiValueField, setSelectedApiValueField] = useState("");

  useEffect(() => {
    if (field) {
      const isNewField = !field.field_id;
      setConfig({
        name: field.name || "",
        label: field.label || "",
        required: field.required || false,
        validation: field.validation || "",
        type: field.type || "",
        options: Array.isArray(field.options)
          ? field.options.join("|")
          : field.options || "",
        options_source: field.options_source || null,
        options_label_field: field.options_label_field || null,
        options_value_field: field.options_value_field || null,
        order: field.order || 1,
      });
      setOptionSourceType(
        field.options_source
          ? optionSources.master.some((s) => s.table === field.options_source)
            ? "master"
            : "api"
          : field.options && field.options.length > 0
          ? "hard_coded"
          : ""
      );
      setSelectedMasterTable(field.options_source || "");
      setSelectedMasterLabelField(field.options_label_field || "");
      setSelectedMasterValueField(field.options_value_field || "");
      setSelectedApiSource(field.options_source || "");
      setSelectedApiLabelField(field.options_label_field || "");
      setSelectedApiValueField(field.options_value_field || "");
    }
  }, [field, optionSources]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSourceTypeChange = (e) => {
    const type = e.target.value;
    setOptionSourceType(type);
    setConfig((prev) => ({
      ...prev,
      options: "",
      options_source: null,
      options_label_field: null,
      options_value_field: null,
    }));
    setSelectedMasterTable("");
    setSelectedMasterLabelField("");
    setSelectedMasterValueField("");
    setSelectedApiSource("");
    setSelectedApiLabelField("");
    setSelectedApiValueField("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!config.label.trim()) {
      handleCustomResponse("error", "Field Label cannot be empty.");
      return;
    }
    if (!config.name) {
      handleCustomResponse("error", "Please select a Field Name.");
      return;
    }
    if (config.type === "select") {
      if (
        optionSourceType === "hard_coded" &&
        (!config.options || config.options.trim() === "")
      ) {
        handleCustomResponse(
          "error",
          "Please add at least one option for hardcoded dropdown."
        );
        return;
      }
      if (
        optionSourceType === "master" &&
        (!selectedMasterTable ||
          !selectedMasterLabelField ||
          !selectedMasterValueField)
      ) {
        handleCustomResponse(
          "error",
          "Please select table, label field, and value field for master data source."
        );
        return;
      }
      if (
        optionSourceType === "api" &&
        (!selectedApiSource || !selectedApiLabelField || !selectedApiValueField)
      ) {
        handleCustomResponse(
          "error",
          "Please select API, label field, and value field for API source."
        );
        return;
      }
    }

    const fieldToSave = {
      ...field,
      ...config,
      options:
        config.type === "select" &&
        optionSourceType === "hard_coded" &&
        config.options
          ? config.options.split("|").map((opt) => opt.trim())
          : null,
      options_source:
        optionSourceType === "master"
          ? selectedMasterTable
          : optionSourceType === "api"
          ? selectedApiSource
          : null,
      options_label_field:
        optionSourceType === "master"
          ? selectedMasterLabelField
          : optionSourceType === "api"
          ? selectedApiLabelField
          : null,
      options_value_field:
        optionSourceType === "master"
          ? selectedMasterValueField
          : optionSourceType === "api"
          ? selectedApiValueField
          : null,
    };

    onSave(fieldToSave);
    onClose();
  };

  if (!isOpen) return null;
  // Select For Field Name
  const options = fieldNames.map((field) => ({
    value: field.field_name,
    label: field.field_name,
  }));

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "400px",
        }}
        className="shadow-sm overflow-auto"
      >
        <h3 className="mb-4">{isEditMode ? "Edit Field" : "Add Field"}</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label fw-bold">Field Label</label>
            <Select
              options={options}
              value={options.find((opt) => opt.value === config.name)}
              onChange={(selectedOption) => {
                handleChange({
                  target: {
                    name: "name",
                    value: selectedOption?.value || "",
                  },
                });
              }}
              placeholder="-- Select Field Name --"
              isSearchable
              className="basic-single"
              classNamePrefix="select"
            />
            {/* <select
              className="form-select"
              name="name"
              value={config.name}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                -- Select Field Name --
              </option>
              {fieldNames.map((field) => (
                <option key={field.field_name_id} value={field.field_name}>
                  {field.field_name}
                </option>
              ))}
            </select> */}
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Field Name</label>
            <input
              type="text"
              className="form-control"
              name="label"
              value={config.label}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-3 form-check d-flex align-items-center">
            <input
              type="checkbox"
              className="form-check-input"
              name="required"
              checked={config.required}
              onChange={handleChange}
            />
            <label className="form-check-label ms-2">Required</label>
          </div>
          {config.type === "select" && (
            <div className="mb-3">
              <label className="form-label fw-bold">Options Source</label>
              <select
                className="form-select"
                name="optionSourceType"
                value={optionSourceType}
                onChange={handleSourceTypeChange}
              >
                <option value="" disabled>
                  -- Select Source Type --
                </option>
                <option value="hard_coded">Write By Own</option>
                {/* <option value="master">Select from master table</option> */}
                <option value="api">Get By API</option>
              </select>
              {optionSourceType === "hard_coded" && (
                <div className="mt-3">
                  <label className="form-label fw-bold">
                    Dropdown Options (pipe-separated)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="options"
                    value={config.options}
                    onChange={handleChange}
                    placeholder="e.g., option1|option2|option3"
                  />
                </div>
              )}
              {optionSourceType === "master" && (
                <>
                  <div className="mt-3">
                    <label className="form-label fw-bold">
                      Select Master Table
                    </label>
                    <select
                      className="form-select"
                      value={selectedMasterTable.table}
                      onChange={(e) => {
                        const selected = JSON.parse(e.target.value);
                        setSelectedMasterTable(selected.table);
                        setSelectedMasterLabelField(selected.columns[1] || "");
                        setSelectedMasterValueField(selected.columns[0] || "");
                      }}
                    >
                      <option value="" disabled>
                        -- Select Table --
                      </option>
                      {optionSources.master.map((source) => (
                        <option
                          key={source.table}
                          value={JSON.stringify(source)}
                        >
                          {source.table}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* {selectedMasterTable && (
                    <>
                      <div className="mt-3">
                        <label className="form-label fw-bold">
                          Label Field
                        </label>
                        <select
                          className="form-select"
                          value={selectedMasterLabelField}
                          onChange={(e) =>
                            setSelectedMasterLabelField(e.target.value)
                          }
                        >
                          <option value="" disabled>
                            -- Select Label Field --
                          </option>
                          {optionSources.master
                            .find((s) => s.table === selectedMasterTable)
                            ?.columns.map((column) => (
                              <option key={column} value={column}>
                                {column}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="mt-3">
                        <label className="form-label fw-bold">
                          Value Field
                        </label>
                        <select
                          className="form-select"
                          value={selectedMasterValueField}
                          onChange={(e) =>
                            setSelectedMasterValueField(e.target.value)
                          }
                        >
                          <option value="" disabled>
                            -- Select Value Field --
                          </option>
                          {optionSources.master
                            .find((s) => s.table === selectedMasterTable)
                            ?.columns.map((column) => (
                              <option key={column} value={column}>
                                {column}
                              </option>
                            ))}
                        </select>
                      </div>
                    </>
                  )} */}
                </>
              )}
              {optionSourceType === "api" && (
                <>
                  <div className="mt-3">
                    <label className="form-label fw-bold">
                      Select API Source
                    </label>
                    <select
                      className="form-select"
                      value={selectedApiSource.url}
                      onChange={(e) => {
                        const selected = JSON.parse(e.target.value);
                        setSelectedApiSource(selected.url);
                        setSelectedApiLabelField(selected.label_field || "");
                        setSelectedApiValueField(selected.value_field || "");
                      }}
                    >
                      <option value="" disabled>
                        -- Select API --
                      </option>
                      {optionSources.api.map((source) => (
                        <option key={source.url} value={JSON.stringify(source)}>
                          {source.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* {selectedApiSource && (
                    <>
                      <div className="mt-3">
                        <label className="form-label fw-bold">
                          Label Field
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedApiLabelField}
                          onChange={(e) =>
                            setSelectedApiLabelField(e.target.value)
                          }
                        />
                      </div>
                      <div className="mt-3">
                        <label className="form-label fw-bold">
                          Value Field
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedApiValueField}
                          onChange={(e) =>
                            setSelectedApiValueField(e.target.value)
                          }
                        />
                      </div>
                    </>
                  )} */}
                </>
              )}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary-600">
              {isEditMode ? "Update Field" : "Add Field"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Step Update Modal
const StepUpdateModal = ({ isOpen, onClose, step, onSave }) => {
  const [stepLabel, setStepLabel] = useState(step?.label || "");
  const [stepLabelError, setStepLabelError] = useState("");

  useEffect(() => {
    if (step) {
      setStepLabel(step.label);
      setStepLabelError("");
    }
  }, [step]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!stepLabel.trim()) {
      setStepLabelError("Step label cannot be empty.");
      return;
    }
    onSave({ ...step, label: stepLabel });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "400px",
        }}
        className="shadow-sm overflow-auto"
      >
        <h3 className="mb-4">Update Step</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label fw-bold">Step Label</label>
            <input
              type="text"
              className={`form-control ${stepLabelError ? "is-invalid" : ""}`}
              value={stepLabel}
              onChange={(e) => {
                setStepLabel(e.target.value);
                setStepLabelError("");
              }}
              required
            />
            {stepLabelError && (
              <div className="invalid-feedback">{stepLabelError}</div>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary-600">
              Update Step
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
const CustomNode = memo(({ id, data }) => {
  const handleDragStart = (event, fieldId) => {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({ fieldId, nodeId: id })
    );
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const rawData = event.dataTransfer.getData("application/json");
    if (!rawData) {
      console.warn("No data found in drag-and-drop operation");
      return;
    }
    let draggedData;
    try {
      draggedData = JSON.parse(rawData);
    } catch (error) {
      console.error("Failed to parse dragged data:", error);
      handleCustomResponse("error", "Invalid drag-and-drop data");
      return;
    }
    const { fieldId: draggedFieldId, nodeId: sourceNodeId } = draggedData;

    if (sourceNodeId !== id) return;

    const targetFieldElement = event.target.closest(".field-item");
    if (!targetFieldElement) return;

    const targetFieldId = targetFieldElement.dataset.fieldId;
    if (!draggedFieldId || !targetFieldId) return;

    const updatedFields = [...data.fields];
    const draggedIndex = updatedFields.findIndex(
      (f) => f.id === draggedFieldId
    );
    const targetIndex = updatedFields.findIndex((f) => f.id === targetFieldId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const [draggedField] = updatedFields.splice(draggedIndex, 1);
    updatedFields.splice(targetIndex, 0, draggedField);

    const reorderedFields = updatedFields.map((f, index) => ({
      ...f,
      order: index + 1,
    }));

    data.onUpdateFields(id, reorderedFields);
  };

  const handleMoveField = (fieldId, direction) => {
    const updatedFields = [...data.fields];
    const index = updatedFields.findIndex((f) => f.id === fieldId);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === updatedFields.length - 1)
    ) {
      return;
    }

    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [updatedFields[index], updatedFields[swapIndex]] = [
      updatedFields[swapIndex],
      updatedFields[index],
    ];

    const reorderedFields = updatedFields.map((f, i) => ({
      ...f,
      order: i + 1,
    }));

    data.onUpdateFields(id, reorderedFields);
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        padding: "0",
        border: "2px solid #e5e7eb",
        borderRadius: "16px",
        width: "320px", // Fixed width
        height: "200px", // Fixed height
        boxShadow: "0 8px 25px rgba(0,0,0,0.08), 0 3px 10px rgba(0,0,0,0.04)",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        e.target.style.boxShadow =
          "0 12px 35px rgba(0,0,0,0.12), 0 6px 15px rgba(0,0,0,0.06)";
        e.target.style.borderColor = "#cbd5e1";
        e.target.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.target.style.boxShadow =
          "0 8px 25px rgba(0,0,0,0.08), 0 3px 10px rgba(0,0,0,0.04)";
        e.target.style.borderColor = "#e5e7eb";
        e.target.style.transform = "translateY(0)";
      }}
    >
      {/* Header Section - Fixed */}
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "16px 20px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 10px rgba(102, 126, 234, 0.2)",
          borderRadius: "14px 14px 0 0",
          flexShrink: 0,
          height: "60px", // Fixed header height
        }}
      >
        <div
          style={{
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: "600",
            letterSpacing: "0.3px",
            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: "6px",
              height: "6px",
              backgroundColor: "#ffffff",
              borderRadius: "50%",
              opacity: "0.8",
              flexShrink: 0,
            }}
          ></div>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data.label}
          </span>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              data.onEditStep(id);
            }}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "8px",
              color: "#ffffff",
              cursor: "pointer",
              padding: "6px 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              fontSize: "12px",
              width: "32px",
              height: "32px",
            }}
            title="Edit Step"
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255,255,255,0.3)";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255,255,255,0.2)";
              e.target.style.transform = "scale(1)";
            }}
          >
            <Edit size={14} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              data.onRemoveNode(id);
            }}
            style={{
              background: "rgba(239,68,68,0.2)",
              border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: "8px",
              color: "#ffffff",
              cursor: "pointer",
              padding: "6px 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              fontSize: "12px",
              width: "32px",
              height: "32px",
            }}
            title="Delete Step"
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(239,68,68,0.3)";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(239,68,68,0.2)";
              e.target.style.transform = "scale(1)";
            }}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Content Section - Scrollable */}
      <div
        className="content-container"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "calc(100% - 60px)",
          position: "relative",
        }}
      >
        {/* Scroll Shadows */}
        <div
          className="scroll-shadow-top"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "8px",
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.06) 0%, transparent 100%)",
            zIndex: 1,
            opacity: 0,
            transition: "opacity 0.3s ease",
            pointerEvents: "none",
          }}
        />
        <div
          className="scroll-shadow-bottom"
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "8px",
            background:
              "linear-gradient(0deg, rgba(0,0,0,0.06) 0%, transparent 100%)",
            zIndex: 1,
            opacity: 0,
            transition: "opacity 0.3s ease",
            pointerEvents: "none",
          }}
        />

        <style>
          {`
            .content-container[data-scroll-top] .scroll-shadow-top {
              opacity: 1;
            }
            .content-container[data-scroll-bottom] .scroll-shadow-bottom {
              opacity: 1;
            }
            .field-item {
              scroll-margin: 8px;
            }
            .custom-scrollbar {
              scroll-padding: 8px;
            }
          `}
        </style>
        {data.fields?.length > 0 ? (
          <div
            className="custom-scrollbar"
            style={{
              padding: "16px 12px 16px 16px",
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              position: "relative",
            }}
            onScroll={(e) => {
              const scrollTop = e.target.scrollTop;
              const maxScroll = e.target.scrollHeight - e.target.clientHeight;
              const scrollPercent = scrollTop / maxScroll;

              // Add scroll shadow effect
              const container = e.target.closest(".content-container");
              if (container) {
                if (scrollTop > 10) {
                  container.setAttribute("data-scroll-top", "true");
                } else {
                  container.removeAttribute("data-scroll-top");
                }

                if (scrollTop < maxScroll - 10) {
                  container.setAttribute("data-scroll-bottom", "true");
                } else {
                  container.removeAttribute("data-scroll-bottom");
                }
              }
            }}
          >
            {/* Custom Scrollbar Styles */}
            <style>
              {`
                .custom-scrollbar {
                  scroll-behavior: smooth;
                  scrollbar-width: thin;
                  scrollbar-color: #cbd5e1 #f8fafc;
                }
                .custom-scrollbar::-webkit-scrollbar {
                  width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                  background: #f8fafc;
                  border-radius: 6px;
                  margin: 4px 0;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  background: linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%);
                  border-radius: 6px;
                  transition: all 0.3s ease;
                  border: 1px solid #f8fafc;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
                  transform: scaleX(1.2);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:active {
                  background: linear-gradient(180deg, #64748b 0%, #475569 100%);
                }
                .custom-scrollbar::-webkit-scrollbar-corner {
                  background: #f8fafc;
                }
              `}
            </style>
            {data.fields
              .sort((a, b) => a.order - b.order)
              .map((field, index) => (
                <div
                  key={field.id}
                  data-field-id={field.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, field.id)}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className="field-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    border: "1px solid #e2e8f0",
                    padding: "12px 14px",
                    borderRadius: "10px",
                    fontSize: "12px",
                    color: "#475569",
                    cursor: "move",
                    transition: "all 0.2s ease",
                    position: "relative",
                    minHeight: "44px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background =
                      "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
                    e.target.style.borderColor = "#cbd5e1";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background =
                      "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
                    e.target.style.borderColor = "#e2e8f0";
                  }}
                >
                  {/* Drag Handle */}
                  <div
                    style={{
                      position: "absolute",
                      left: "6px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                      opacity: "0.4",
                    }}
                  >
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        style={{
                          width: "3px",
                          height: "3px",
                          backgroundColor: "#94a3b8",
                          borderRadius: "50%",
                        }}
                      ></div>
                    ))}
                  </div>

                  {/* Field Label */}
                  <span
                    style={{
                      fontWeight: "500",
                      fontSize: "12px",
                      color: "#1e293b",
                      marginLeft: "18px",
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        width: "4px",
                        height: "4px",
                        backgroundColor: "#3b82f6",
                        borderRadius: "50%",
                        opacity: "0.7",
                        flexShrink: 0,
                      }}
                    ></div>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {field.label || field.name}
                    </span>
                    {field.required && (
                      <span
                        style={{
                          color: "#ef4444",
                          fontWeight: "600",
                          fontSize: "12px",
                          flexShrink: 0,
                        }}
                      >
                        *
                      </span>
                    )}
                  </span>

                  {/* Action Buttons */}
                  <div
                    style={{
                      display: "flex",
                      gap: "2px",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveField(field.id, "up");
                      }}
                      disabled={index === 0}
                      style={{
                        background: "none",
                        border: "none",
                        color: index === 0 ? "#cbd5e1" : "#64748b",
                        cursor: index === 0 ? "not-allowed" : "pointer",
                        padding: "2px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        opacity: index === 0 ? "0.5" : "1",
                        width: "20px",
                        height: "20px",
                      }}
                      title="Move Up"
                      onMouseEnter={(e) => {
                        if (index !== 0) {
                          e.target.style.backgroundColor = "#f1f5f9";
                          e.target.style.color = "#3b82f6";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.color =
                          index === 0 ? "#cbd5e1" : "#64748b";
                      }}
                    >
                      <ChevronUp size={10} />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveField(field.id, "down");
                      }}
                      disabled={index === data.fields.length - 1}
                      style={{
                        background: "none",
                        border: "none",
                        color:
                          index === data.fields.length - 1
                            ? "#cbd5e1"
                            : "#64748b",
                        cursor:
                          index === data.fields.length - 1
                            ? "not-allowed"
                            : "pointer",
                        padding: "2px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        opacity: index === data.fields.length - 1 ? "0.5" : "1",
                        width: "20px",
                        height: "20px",
                      }}
                      title="Move Down"
                      onMouseEnter={(e) => {
                        if (index !== data.fields.length - 1) {
                          e.target.style.backgroundColor = "#f1f5f9";
                          e.target.style.color = "#3b82f6";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.color =
                          index === data.fields.length - 1
                            ? "#cbd5e1"
                            : "#64748b";
                      }}
                    >
                      <ChevronDown size={10} />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        data.onEditField(field, id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#64748b",
                        cursor: "pointer",
                        padding: "2px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        width: "20px",
                        height: "20px",
                      }}
                      title="Edit Field"
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#f1f5f9";
                        e.target.style.color = "#3b82f6";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.color = "#64748b";
                      }}
                    >
                      <Edit size={10} />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        data.onDeleteField(field.id, id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#64748b",
                        cursor: "pointer",
                        padding: "2px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        width: "20px",
                        height: "20px",
                      }}
                      title="Delete Field"
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#fef2f2";
                        e.target.style.color = "#ef4444";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.color = "#64748b";
                      }}
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              color: "#9ca3af",
              fontSize: "11px",
              textAlign: "center",
              background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
              borderRadius: "8px",
              border: "1px dashed #d1d5db",
              margin: "16px",
              flex: 1,
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  background:
                    "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)",
                  borderRadius: "2px",
                  opacity: "0.6",
                }}
              ></div>
            </div>
            <div
              style={{
                fontWeight: "500",
                marginBottom: "2px",
                color: "#6b7280",
                fontSize: "11px",
              }}
            >
              No fields yet
            </div>
            <div
              style={{
                fontSize: "10px",
                color: "#9ca3af",
              }}
            >
              Add fields to get started
            </div>
          </div>
        )}
      </div>

      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "2px solid #ffffff",
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "2px solid #ffffff",
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        }}
      />
    </div>
  );
});

// Main EditWorkFlow Component
const WorkFlowEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { workflow } = location.state || {};
  const workflowId = workflow?.workflow_id || null;
  const workflowName = workflow?.name || "No Workflow Selected";
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [stepLabel, setStepLabel] = useState("");
  const [stepLabelError, setStepLabelError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [fieldConfigModal, setFieldConfigModal] = useState({
    isOpen: false,
    field: null,
    isEditMode: false,
    nodeId: null,
  });
  const [stepUpdateModal, setStepUpdateModal] = useState({
    isOpen: false,
    step: null,
    nodeId: null,
  });
  const [deletedNodes, setDeletedNodes] = useState([]);
  const [optionSources, setOptionSources] = useState({ master: [], api: [] });
  const [fieldNames, setFieldNames] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const saveToHistory = useCallback(
    (newNodes, newEdges) => {
      setHistory((prev) => {
        const newHistory = prev.slice(0, historyIndex + 1);
        return [
          ...newHistory,
          {
            nodes: JSON.parse(JSON.stringify(newNodes)),
            edges: JSON.parse(JSON.stringify(newEdges)),
          },
        ];
      });
      setHistoryIndex((prev) => prev + 1);
    },
    [historyIndex]
  );

  const nodeTypes = {
    custom: (props) => (
      <CustomNode
        {...props}
        data={{
          ...props.data,
          onUpdateFields: handleUpdateFields,
          onRemoveNode: handleRemoveNode,
          onEditField: handleEditField,
          onDeleteField: handleDeleteField,
          onEditStep: handleEditStep,
        }}
      />
    ),
  };

  const updateEdges = useCallback(
    (nodesToUpdate) => {
      const sortedNodes = [...nodesToUpdate].sort(
        (a, b) => a.data.step_order - b.data.step_order
      );
      const newEdges = sortedNodes.slice(0, -1).map((node, index) => ({
        id: `edge-${node.id}-${sortedNodes[index + 1].id}`,
        source: node.id,
        target: sortedNodes[index + 1].id,
        animated: true,
        style: { stroke: "black" },
      }));
      setEdges(newEdges);
      saveToHistory(nodesToUpdate, newEdges);
    },
    [setEdges, saveToHistory]
  );

  const fetchWorkFlow = useCallback(async () => {
    if (!workflowId) {
      handleCustomResponse("error", "Invalid Workflow ID");
      navigate("/workflow");
      return;
    }
    setIsLoading(true);
    try {
      const res = await apiCall(
        "get",
        `/workflow-steps-fields/${workflowId}`,
        null,
        navigate
      );
      const workflowData = res.data.data;
      const steps = workflowData.steps || [];

      const newNodes = steps.map((step, index) => ({
        id: String(step.step_id),
        type: "custom",
        position: { x: 100, y: index * 250 },
        data: {
          step_id: step.step_id,
          code: step.step_code,
          label: step.step_label,
          step_order: step.step_order,
          fields:
            step.fields?.map((field, idx) => ({
              id: String(field.field_id || `client-${Date.now()}-${idx}`),
              field_id: field.field_id,
              name: field.name,
              label: field.label,
              type: field.type,
              required: field.required,
              validation: field.validation,
              options:
                field.options && field.options !== "null"
                  ? JSON.parse(field.options)
                  : null,
              options_source: field.options_source,
              options_label_field: field.options_label_field,
              options_value_field: field.options_value_field,
              order: field.order || idx + 1,
            })) || [],
        },
      }));

      setNodes(newNodes);
      updateEdges(newNodes);
      saveToHistory(newNodes, edges);

      const handleRes = res.data;
      handleApiResponse(handleRes, "success", "GET");
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
    } finally {
      setIsLoading(false);
    }
  }, [workflowId, navigate, setNodes, updateEdges, saveToHistory]);

  const fetchOptionSources = useCallback(async () => {
    try {
      const [masterRes, apiRes, fieldNamesRes] = await Promise.all([
        apiCall("get", `/get-option-source-data/1`, null, navigate),
        apiCall("get", `/get-option-source-data/2`, null, navigate),
        apiCall("get", `/workflow-field-names`, null, navigate),
      ]);
      setOptionSources({
        master: masterRes.data.data,
        api: apiRes.data.data,
      });
      setFieldNames(fieldNamesRes.data.data);
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
    }
  }, [navigate]);

  useEffect(() => {
    fetchWorkFlow();
    fetchOptionSources();
  }, []);

  const validateStep = (label, excludeNodeId = null) => {
    const errors = { label: "" };
    if (!label) errors.label = "Step label is required";
    if (
      nodes.some(
        (node) => node.data.label === label && node.id !== excludeNodeId
      )
    )
      errors.label = "Step label already exists";
    return errors;
  };

  const handleAddStep = () => {
    const errors = validateStep(stepLabel);
    if (errors.label) {
      setStepLabelError(errors.label);
      return;
    }
    setStepLabelError("");

    const newCode = generateUniqueCode(
      stepLabel,
      nodes.map((n) => n.data.code)
    );
    const newNode = {
      id: `node-${uuidv4()}`,
      type: "custom",
      position: { x: 100, y: nodes.length * 250 },
      data: {
        step_id: null,
        code: newCode,
        label: stepLabel,
        step_order: nodes.length + 1,
        fields: [],
      },
    };

    setNodes((nds) => {
      const updatedNodes = [...nds, newNode];
      updateEdges(updatedNodes);
      return updatedNodes;
    });

    setStepLabel("");
    handleCustomResponse(
      "success",
      `Step "${stepLabel}" added with code "${newCode}"`
    );
  };

  const handleEditStep = (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (node) {
      setStepUpdateModal({
        isOpen: true,
        step: node.data,
        nodeId: nodeId,
      });
    }
  };

  const handleSaveStep = (updatedStep) => {
    const errors = validateStep(updatedStep.label, stepUpdateModal.nodeId);
    if (errors.label) {
      handleCustomResponse("error", `${errors.label}`);
      return;
    }

    const newCode = generateUniqueCode(
      updatedStep.label,
      nodes
        .filter((n) => n.id !== stepUpdateModal.nodeId)
        .map((n) => n.data.code)
    );

    setNodes((nds) => {
      const updatedNodes = nds.map((node) =>
        node.id === stepUpdateModal.nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                label: updatedStep.label,
                code: newCode,
              },
            }
          : node
      );
      saveToHistory(updatedNodes, edges);
      return updatedNodes;
    });
    handleCustomResponse("success", `Step "${updatedStep.label}" updated.`);
  };

  const handleSaveFieldConfig = (updatedField) => {
    const { nodeId } = fieldConfigModal;
    setNodes((nds) => {
      const updatedNodes = nds.map((node) => {
        if (node.id === nodeId) {
          const isDuplicate = node.data.fields.some(
            (f) => f.name === updatedField.name && f.id !== updatedField.id
          );
          if (isDuplicate) {
            handleCustomResponse(
              "error",
              "Field name already exists in this step."
            );
            return node;
          }

          const newFields = fieldConfigModal.isEditMode
            ? node.data.fields.map((f) =>
                f.id === updatedField.id
                  ? { ...updatedField, order: f.order }
                  : f
              )
            : [
                ...node.data.fields,
                {
                  ...updatedField,
                  id: `new-${uuidv4()}`,
                  field_id: null,
                  order: node.data.fields.length + 1,
                },
              ];

          const sortedFields = newFields
            .sort((a, b) => a.order - b.order)
            .map((f, index) => ({ ...f, order: index + 1 }));

          return {
            ...node,
            data: { ...node.data, fields: sortedFields },
          };
        }
        return node;
      });
      saveToHistory(updatedNodes, edges);
      return updatedNodes;
    });
    const msg = fieldConfigModal.isEditMode ? "Field updated" : "Field added";
    handleCustomResponse("success", msg);
  };

  const handleEditField = (field, nodeId) => {
    setFieldConfigModal({
      isOpen: true,
      field: field,
      isEditMode: true,
      nodeId: nodeId,
    });
  };

  const handleDeleteField = useCallback(
    (fieldId, nodeId) => {
      setNodes((nds) => {
        const updatedNodes = nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  fields: node.data.fields
                    .filter((f) => f.id !== fieldId)
                    .map((f, index) => ({
                      ...f,
                      order: index + 1,
                    })),
                },
              }
            : node
        );
        saveToHistory(updatedNodes, edges);
        return updatedNodes;
      });
      handleCustomResponse("success", "Field removed successfully");
    },
    [setNodes, edges, saveToHistory]
  );

  const handleUpdateFields = useCallback(
    (nodeId, reorderedFields) => {
      const sortedFields = reorderedFields.map((field, index) => ({
        ...field,
        order: index + 1,
      }));
      setNodes((nds) => {
        const updatedNodes = nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: { ...node.data, fields: sortedFields },
            };
          }
          return node;
        });
        saveToHistory(updatedNodes, edges);
        return updatedNodes;
      });
      handleCustomResponse("success", "Fields reordered within step.");
    },
    [setNodes, edges, saveToHistory]
  );

  const handleRemoveNode = useCallback(
    (nodeId) => {
      setNodes((nds) => {
        const nodeToRemove = nds.find((node) => node.id === nodeId);
        if (nodeToRemove) {
          setDeletedNodes((prevDeleted) => [...prevDeleted, nodeToRemove]);
          const updatedNodes = nds
            .filter((node) => node.id !== nodeId)
            .map((node, index) => ({
              ...node,
              data: { ...node.data, step_order: index + 1 },
              position: { x: 100, y: index * 250 },
            }));
          updateEdges(updatedNodes);
          handleCustomResponse(
            "success",
            `Step "${nodeToRemove.data.label}" moved to trash.`
          );
          setSelectedNode(null);
          return updatedNodes;
        }
        return nds;
      });
    },
    [setNodes, setDeletedNodes, updateEdges]
  );

  const handleRestoreNode = useCallback(
    (nodeId) => {
      setDeletedNodes((prevDeleted) => {
        const nodeToRestore = prevDeleted.find((node) => node.id === nodeId);
        if (nodeToRestore) {
          const errors = validateStep(nodeToRestore.data.label);
          if (errors.label) {
            handleCustomResponse("error", `Cannot restore: ${errors.label}`);
            return prevDeleted;
          }

          setNodes((nds) => {
            const updatedNodes = [
              ...nds,
              {
                ...nodeToRestore,
                data: { ...nodeToRestore.data, step_order: nds.length + 1 },
                position: { x: 100, y: nds.length * 250 },
              },
            ];
            updateEdges(updatedNodes);
            return updatedNodes;
          });
          handleCustomResponse(
            "success",
            `Step "${nodeToRestore.data.label}" restored.`
          );
          return prevDeleted.filter((node) => node.id !== nodeId);
        }
        return prevDeleted;
      });
    },
    [setNodes, setDeletedNodes, updateEdges, validateStep]
  );

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex((prev) => prev - 1);
      const previousState = history[historyIndex - 1];
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      handleCustomResponse("info", "Action undone");
    } else {
      handleCustomResponse("info", "No more actions to undo");
    }
  }, [history, historyIndex, setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex((prev) => prev + 1);
      const nextState = history[historyIndex + 1];
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      handleCustomResponse("info", "Action redone");
    } else {
      handleCustomResponse("info", "No more actions to redo");
    }
  }, [history, historyIndex, setNodes, setEdges]);

  const handleSaveWorkflow = async () => {
    setSaveStatus("saving");
    setIsLoading(true);
    try {
      const sortedNodes = [...nodes].sort(
        (a, b) => a.data.step_order - b.data.step_order
      );
      const workflowData = {
        workflow_id: workflowId,
        steps: sortedNodes.map((node) => ({
          step_id: node.data.step_id || null,
          step_code: node.data.code,
          step_label: node.data.label,
          step_order: node.data.step_order,
          fields: node.data.fields
            .sort((a, b) => a.order - b.order)
            .map((field, index) => ({
              field_id: field.field_id || null,
              name: field.name,
              label: field.label,
              type: field.type,
              required: field.required,
              validation: field.validation,
              options: field.options ? field.options : null,
              options_source: field.options_source || null,
              options_label_field: field.options_label_field || null,
              options_value_field: field.options_value_field || null,
              field_order: index + 1,
            })),
        })),
      };

      const res = await apiCall(
        "post",
        "/insert-workflow-step-fields",
        workflowData,
        navigate
      );
      if (res.data.success) {
        const handleRes = res.data;
        handleApiResponse(handleRes, "success", "POST");
        setSaveStatus("success");
        setNodes((prevNodes) =>
          prevNodes.map((node) => {
            const savedStep = res.data.steps?.find(
              (s) => s.tempId === node.id || s.step_id === node.data.step_id
            );
            if (savedStep) {
              return {
                ...node,
                id: String(savedStep.step_id),
                data: {
                  ...node.data,
                  step_id: savedStep.step_id,
                  fields: node.data.fields.map((field) => {
                    const savedField = savedStep.fields?.find(
                      (f) =>
                        f.tempId === field.id || f.field_id === field.field_id
                    );
                    if (savedField) {
                      return {
                        ...field,
                        id: String(savedField.field_id),
                        field_id: savedField.field_id,
                        order: savedField.order,
                      };
                    }
                    return field;
                  }),
                },
              };
            }
            return node;
          })
        );
        await fetchWorkFlow();
      } else {
        handleCustomResponse("error", "Failed to save workflow");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
      setSaveStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  const onDragStart = (event, component) => {
    event.dataTransfer.setData(
      "application/reactflow",
      JSON.stringify(component)
    );
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowInstance || !selectedNode) {
        handleCustomResponse("error", "Please select a step to add fields to.");
        return;
      }

      const component = JSON.parse(
        event.dataTransfer.getData("application/reactflow")
      );
      const nextFieldOrder = selectedNode.data.fields.length + 1;

      setFieldConfigModal({
        isOpen: true,
        field: { ...component, order: nextFieldOrder },
        isEditMode: false,
        nodeId: selectedNode.id,
      });
     handleCustomResponse("info", `Configuring new ${component.type} field.`);
    },
    [reactFlowInstance, selectedNode]
  );

  const onNodeDragStop = useCallback(
    (event, draggedNode) => {
      setNodes((nds) => {
        const sortedNodes = [...nds].sort(
          (a, b) => a.position.y - b.position.y
        );
        const reorderedNodes = sortedNodes.map((node, index) => ({
          ...node,
          data: { ...node.data, step_order: index + 1 },
          // position: { x: 100, y: index * 250 },
        }));
        updateEdges(reorderedNodes);
        return reorderedNodes;
      });
    },
    [setNodes, updateEdges]
  );

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) => {
        const newEdges = addEdge(
          { ...params, animated: true, style: { stroke: "black" } },
          eds
        );
        saveToHistory(nodes, newEdges);
        return newEdges;
      }),
    [setEdges, nodes, saveToHistory]
  );

  const handleNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <div className="container-fluid vh-100 d-flex flex-column">
      <div className="row p-3 shadow-sm bg-white">
        <div className="col-6 d-flex align-items-center">
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/workflow", { replace: true })}
          >
            <div className="d-flex align-items-center gap-3">
              <Navigation /> <span>Back</span>
            </div>
          </button>
        </div>
        <div className="col-6 text-end">
          <button
            className="btn btn-outline-secondary me-2"
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            title="Undo"
          >
            <Undo size={16} />
          </button>
          <button
            className="btn btn-outline-secondary me-2"
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            title="Redo"
          >
            <Redo size={16} />
          </button>
          <button
            className="btn btn-primary-600"
            onClick={handleSaveWorkflow}
            disabled={isLoading || saveStatus === "saving"}
          >
            <div className="d-flex align-items-center gap-1">
              <Save size={16} className="me-2" />
              {isLoading || saveStatus === "saving"
                ? "Saving..."
                : "Save Workflow"}
            </div>
          </button>
        </div>
      </div>
      <div className="row" style={{ flex: 1, minHeight: 0 }}>
        <div className="col-md-2 border-end p-3 shadow-sm bg-white">
          <span className="text-primary-600 fw-bold">Add Step</span>
          <div className="mb-3">
            <input
              type="text"
              className={`form-control ${stepLabelError ? "is-invalid" : ""}`}
              placeholder="Step Label"
              value={stepLabel}
              onChange={(e) => {
                setStepLabel(e.target.value);
                setStepLabelError("");
              }}
              disabled={isLoading}
            />
            {stepLabelError && (
              <div className="invalid-feedback">{stepLabelError}</div>
            )}
          </div>
          <button
            className="btn btn-primary-600 w-100"
            onClick={handleAddStep}
            disabled={isLoading}
          >
            Add Step
          </button>
          <hr className="my-3" />
          <span className="text-primary-600 fw-bold">Deleted Steps</span>
          {deletedNodes.length > 0 ? (
            <div
              className="mt-2"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {deletedNodes.map((node) => (
                <div
                  key={node.id}
                  className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
                  style={{ backgroundColor: "#f8d7da" }}
                >
                  <span className="text-sm">{node.data.label}</span>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleRestoreNode(node.id)}
                    title="Restore Step"
                  >
                    <CornerUpLeft size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="small text-muted mt-2">No deleted steps.</p>
          )}
        </div>
        <div className="col-md-8" ref={reactFlowWrapper}>
          <h1 className="text-center text-primary-600 fw-bolder">
            {workflowName}
          </h1>

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeDragStop={onNodeDragStop}
            onNodeClick={handleNodeClick}
            nodeTypes={nodeTypes}
            fitView
          >
            <MiniMap />
            <Controls />
            <Background color="#ccc" gap={16} />
          </ReactFlow>
        </div>
        <div
          className="col-md-2 border-start p-3 shadow-sm bg-white"
          style={{ overflowY: "auto" }}
        >
          {selectedNode ? (
            <div>
              <span className="text-primary-600 fw-bold">
                Available Components
              </span>
              <p className="small">
                Drag a field onto the selected step:{" "}
                <strong>{selectedNode.data.label}</strong>
              </p>
              <div className="overflow-auto">
                {htmlComponents.map((component) => {
                  const IconComponent = component.icon;
                  return (
                    <button
                      key={component.step_field_id}
                      className="btn btn-outline-secondary w-100 mb-2"
                      draggable
                      onDragStart={(event) => onDragStart(event, component)}
                      style={{ cursor: "grab", textAlign: "left" }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <IconComponent size={16} />
                        <span className="text-sm small">
                          {component.displayName}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <span className="text-primary-600 fw-bold">
                Available Components
              </span>
              <p className="small">Select a step to add fields.</p>
              <div className="overflow-auto">
                {htmlComponents.map((component) => {
                  const IconComponent = component.icon;
                  return (
                    <button
                      key={component.step_field_id}
                      className="btn btn-outline-secondary w-100 mb-2"
                      draggable
                      style={{ cursor: "grab", textAlign: "left" }}
                      disabled
                    >
                      <div className="d-flex align-items-center gap-2">
                        <IconComponent size={16} />
                        <span className="text-sm small">
                          {component.displayName}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
      <FieldConfigModal
        isOpen={fieldConfigModal.isOpen}
        onClose={() => {
          setFieldConfigModal({
            isOpen: false,
            field: null,
            isEditMode: false,
            nodeId: null,
          });
          if (fieldConfigModal.field && !fieldConfigModal.isEditMode) {
            setNodes((nds) =>
              nds.map((node) =>
                node.id === fieldConfigModal.nodeId
                  ? {
                      ...node,
                      data: {
                        ...node.data,
                        fields: node.data.fields
                          .filter((f) => f.id !== fieldConfigModal.field.id)
                          .map((f, index) => ({ ...f, order: index + 1 })),
                      },
                    }
                  : node
              )
            );
            saveToHistory(nodes, edges);
            handleCustomResponse("info", "New field discarded.");
          }
        }}
        field={fieldConfigModal.field}
        onSave={handleSaveFieldConfig}
        isEditMode={fieldConfigModal.isEditMode}
        fieldNames={fieldNames}
        optionSources={optionSources}
      />
      <StepUpdateModal
        isOpen={stepUpdateModal.isOpen}
        onClose={() =>
          setStepUpdateModal({ isOpen: false, step: null, nodeId: null })
        }
        step={stepUpdateModal.step}
        onSave={handleSaveStep}
      />
    </div>
  );
};

export default WorkFlowEdit;
